import React, { useState, useContext, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../API";

export const UserContext = React.createContext({
  user: {
    token: "",
    exp: 0,
  },
  singIn: async () => {},
  logoutUser: () => {},
  singUp: async () => {},
  loading: false,
  error: "",
  handleRefreshUser: () => {},
});

const useUser = () => {
  return useContext(UserContext);
};

/**
 * @author Giuliano Antonanzas
 */
export const UserCustomerContextProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState({
    token: localStorage.getItem("NFTdoorUserLOToken"),
    exp: localStorage.getItem("NFTdoorUserLOTokenExp"),
  });
  const [loading, setLoading] = useState();
  const [error, setError] = useState();

  const logout = useCallback(() => {
    setUser(undefined);
    localStorage.removeItem("NFTdoorUserLOToken");
  }, []);

  const singIn = useCallback(async (email, password) => {
    setLoading(true);
    /**@type {{data:{idToken:{jwtToken:string}}}} */
    const response = await API.post({
      url: "/login",
      data: {
        email,
        password,
      },
    });
    if ("error" in response) {
      setLoading(false);
      setError(response.error);
    } else {
      setUser({
        token: response?.data?.idToken?.jwtToken,
        exp: response?.data?.idToken?.payload.exp * 1000,
      });
      setLoading(false);
      localStorage.setItem(
        "NFTdoorUserLOToken",
        response.data?.idToken?.jwtToken
      );
      localStorage.setItem(
        "NFTdoorUserLOTokenExp",
        response.data?.idToken?.payload.exp * 1000
      );
      navigate("/");
    }
  }, []);

  const handleLoadUser = () => {
    if (user.token && Number(user.exp) > new Date().getTime())
      API.get({
        url: "/user",
      }).then((result) => {
        setUser((prev) => ({ ...prev, ...result?.data }));
      });
  };

  const handleRefreshUser = () => {
    handleLoadUser();
  };

  const singUp = useCallback(async (email, password, repeatPassword, code) => {
    setLoading(true);
    /**@type {{data:{idToken:{jwtToken:string}}}} */
    const response = await API.post({
      url: "/register",
      data: {
        password,
        repeatPassword,
        inviteCode: code,
      },
    });

    if ("error" in response) {
      setLoading(false);
      setError(response.error);
    } else {
      singIn(email, password);
    }
  }, []);

  useEffect(() => {
    handleLoadUser();
  }, [user.token, user.exp]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        logout,
        singIn,
        singUp,
        loading,
        error,
        handleRefreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default useUser;
